import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { CheckCircle, Target, Crown, Building, Bot, Star } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuth";
import type { ResellerConfig, AiPricingTier } from "@shared/schema";
import { FEATURE_REGISTRY } from "@shared/schema";

interface PlanFeatures {
  menuAccess?: Record<string, boolean>;
  limits?: Record<string, number>;
  toggles?: Record<string, boolean>;
}

interface SubscriptionPlan {
  id: string;
  name: string;
  displayName: string;
  description: string;
  price: number;
  interval: string;
  features: string[];
  planFeatures?: PlanFeatures;
  storageLimitMb?: number;
  stripePriceId: string;
  isActive: boolean;
  isFeatured: boolean;
  displayOrder: number;
}

const LIMIT_LABELS: Record<string, string> = {
  max_goals: "Max Goals",
  max_tasks: "Max Tasks",
  max_habits: "Max Habits",
  max_milestones: "Max Milestones",
  max_journal_entries: "Max Journal Entries",
  max_notes: "Max Notes",
  max_checklists: "Max Checklists",
  max_media_files: "Max Media Files",
};

const MENU_LABELS: Record<string, string> = {
  dashboard: "Dashboard",
  goals: "Goals & Milestones",
  tasks: "Task Management",
  habits: "Habit Tracking",
  planner: "Planner",
  journal: "Journal",
  notepad: "Notepad",
  intentions: "Intentions",
  gratefuls: "Gratitude Journal",
  checklists: "Checklists",
  media: "Media Library",
  drive: "Google Drive",
  community: "Community",
  assistants: "Assistants",
  calendar: "Calendar",
  meditation: "Meditation",
  incantations: "Identity",
  analytics: "Analytics",
  achievements: "Achievements",
  tours: "Guided Tours",
};

const TOGGLE_LABELS: Record<string, string> = {
  ai_features: "AI Features",
  calendar_sync: "Calendar Sync",
  google_calendar: "Google Calendar",
  custom_branding: "Custom Branding",
  api_access: "API Access",
};

function getEnabledFeatures(plan: SubscriptionPlan): string[] {
  const pf = plan.planFeatures;
  if (!pf) {
    return plan.features || [];
  }
  const items: string[] = [];
  // Enabled menu items
  FEATURE_REGISTRY.menuItems.forEach((key) => {
    if (pf.menuAccess?.[key]) items.push(MENU_LABELS[key] || key);
  });
  // Enabled toggles
  FEATURE_REGISTRY.toggles.forEach((key) => {
    if (pf.toggles?.[key]) items.push(TOGGLE_LABELS[key] || key);
  });
  return items;
}

function hasLimits(plan: SubscriptionPlan): boolean {
  const limits = plan.planFeatures?.limits;
  if (!limits) return false;
  return FEATURE_REGISTRY.limits.some((k) => limits[k] !== undefined && limits[k] !== null);
}

export default function PublicPricing() {
  const { isAuthenticated } = useAuth();
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);

  const { data: allPlans = [], isLoading } = useQuery<SubscriptionPlan[]>({
    queryKey: ["/api/subscription-plans"],
  });

  const { data: config } = useQuery<ResellerConfig>({
    queryKey: ["/api/reseller/config"],
    retry: false,
  });

  const { data: aiPricingTiers = [] } = useQuery<AiPricingTier[]>({
    queryKey: ["/api/reseller/ai-pricing"],
    retry: false,
  });

  const plans = allPlans
    .filter((p) => p.isActive)
    .sort((a, b) => a.displayOrder - b.displayOrder);

  const companyName = config?.companyName || "GoalVia";
  const primaryColor = config?.primaryColor || "#7c3aed";
  const customPricingMessage = config?.customPricingMessage;

  const handleSubscribe = async (planId: string) => {
    if (!isAuthenticated) {
      window.location.href = "/api/login";
      return;
    }
    try {
      setSelectedPlan(planId);
      const response = await fetch("/api/create-subscription", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ planId }),
      });
      if (!response.ok) throw new Error("Failed to create subscription");
      const { clientSecret } = await response.json();
      window.location.href = `/subscribe?client_secret=${clientSecret}`;
    } catch {
      setSelectedPlan(null);
    }
  };

  const getPlanIcon = (name: string) => {
    switch (name.toLowerCase()) {
      case "free": return <Target className="h-6 w-6" />;
      case "pro": return <Crown className="h-6 w-6" />;
      case "enterprise": return <Building className="h-6 w-6" />;
      default: return <Target className="h-6 w-6" />;
    }
  };

  const getPlanColor = (name: string) => {
    switch (name.toLowerCase()) {
      case "free": return "text-gray-500";
      case "pro": return "text-primary";
      case "enterprise": return "text-purple-600";
      default: return "text-gray-500";
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-violet-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800">
      {/* Header */}
      <header className="border-b bg-white/80 dark:bg-gray-900/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-2">
              {config?.companyLogo ? (
                <img src={config.companyLogo} alt={companyName} className="h-8 w-auto" />
              ) : (
                <Target className="h-8 w-8" style={{ color: primaryColor }} />
              )}
              <span className="text-2xl font-bold" style={{ color: primaryColor }}>{companyName}</span>
            </div>
            <div className="flex items-center space-x-3">
              {!isAuthenticated ? (
                <>
                  <Button variant="ghost" onClick={() => { window.location.href = "/api/login"; }}>
                    Sign In
                  </Button>
                  <Button
                    onClick={() => { window.location.href = "/api/login"; }}
                    style={{ backgroundColor: primaryColor }}
                    className="text-white"
                  >
                    Get Started Free
                  </Button>
                </>
              ) : (
                <Button onClick={() => { window.location.href = "/dashboard"; }} style={{ backgroundColor: primaryColor }} className="text-white">
                  Go to Dashboard
                </Button>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="py-16 text-center">
        <div className="container mx-auto px-4">
          <h1 className="text-5xl font-extrabold text-gray-900 dark:text-white mb-4">
            Simple, transparent pricing
          </h1>
          <p className="text-xl text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
            Start free and upgrade as you grow. No hidden fees, cancel anytime.
          </p>
          {customPricingMessage && (
            <p className="mt-4 text-lg font-medium" style={{ color: primaryColor }}>
              {customPricingMessage}
            </p>
          )}
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="pb-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`grid gap-8 max-w-6xl mx-auto items-start ${plans.length === 1 ? "md:grid-cols-1 max-w-sm" : plans.length === 2 ? "md:grid-cols-2 max-w-3xl" : "md:grid-cols-3"}`}>
            {plans.map((plan) => {
              const enabledFeatures = getEnabledFeatures(plan);
              const limits = plan.planFeatures?.limits || {};
              const showLimits = hasLimits(plan);
              const storageMb = plan.storageLimitMb ?? 0;

              return (
                <Card
                  key={plan.id}
                  className={`relative flex flex-col transition-all ${plan.isFeatured ? "border-2 shadow-xl" : "hover:shadow-lg"}`}
                  style={plan.isFeatured ? { borderColor: primaryColor } : {}}
                >
                  {plan.isFeatured && (
                    <Badge
                      className="absolute -top-3 left-1/2 -translate-x-1/2 text-white"
                      style={{ backgroundColor: primaryColor }}
                    >
                      <Star className="w-3 h-3 mr-1" />
                      Most Popular
                    </Badge>
                  )}
                  <CardHeader className="text-center pb-2">
                    <div className={`mx-auto mb-3 ${getPlanColor(plan.name)}`}>
                      {getPlanIcon(plan.name)}
                    </div>
                    <CardTitle className="text-2xl">{plan.displayName}</CardTitle>
                    <CardDescription className="min-h-[2.5rem]">{plan.description}</CardDescription>
                    <div className="mt-4">
                      <span className="text-4xl font-extrabold text-gray-900 dark:text-white">
                        {plan.price === 0 ? "Free" : `$${(plan.price / 100).toFixed(2)}`}
                      </span>
                      {plan.price > 0 && (
                        <span className="text-gray-500 ml-1">/{plan.interval}</span>
                      )}
                    </div>
                  </CardHeader>

                  <CardContent className="flex flex-col flex-1 space-y-4">

                    {/* Storage */}
                    {storageMb > 0 && (
                      <div className="flex items-center justify-between text-sm border-b pb-2">
                        <span className="text-muted-foreground">Storage Limit</span>
                        <span className="font-semibold">{storageMb >= 1024 ? `${(storageMb / 1024).toFixed(0)} GB` : `${storageMb} MB`}</span>
                      </div>
                    )}

                    {/* Resource limits */}
                    {showLimits && (
                      <div className="space-y-1">
                        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Resource Limits</p>
                        {FEATURE_REGISTRY.limits.map((key) => {
                          const val = limits[key];
                          if (val === undefined || val === null) return null;
                          return (
                            <div key={key} className="flex items-center justify-between text-sm">
                              <span className="text-muted-foreground">{LIMIT_LABELS[key] || key}</span>
                              <span className="font-medium">{val === 0 ? "Unlimited" : val.toLocaleString()}</span>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {/* Enabled features */}
                    {enabledFeatures.length > 0 ? (
                      <div className="space-y-1.5">
                        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Included Features</p>
                        {enabledFeatures.map((feature, i) => (
                          <div key={i} className="flex items-start space-x-2">
                            <CheckCircle className="h-4 w-4 mt-0.5 flex-shrink-0" style={{ color: primaryColor }} />
                            <span className="text-sm text-gray-700 dark:text-gray-300">{feature}</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      !showLimits && (
                        <p className="text-sm text-muted-foreground italic">Core features included</p>
                      )
                    )}

                    <div className="mt-auto pt-2">
                      <Button
                        className="w-full text-white"
                        style={{ backgroundColor: primaryColor }}
                        onClick={() => handleSubscribe(plan.id)}
                        disabled={selectedPlan === plan.id}
                      >
                        {selectedPlan === plan.id
                          ? "Processing…"
                          : plan.price === 0
                          ? "Get Started Free"
                          : "Subscribe Now"}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* AI Pricing Tiers */}
          {aiPricingTiers.filter((t) => t.isActive).length > 0 && (
            <div className="mt-24">
              <div className="text-center mb-12">
                <div className="flex items-center justify-center gap-2 mb-3">
                  <Bot className="h-8 w-8" style={{ color: primaryColor }} />
                  <h2 className="text-3xl font-bold text-gray-900 dark:text-white">AI Add-Ons</h2>
                </div>
                <p className="text-lg text-gray-600 dark:text-gray-300">
                  Supercharge your productivity with AI-powered features
                </p>
              </div>
              <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
                {aiPricingTiers
                  .filter((t) => t.isActive)
                  .sort((a, b) => a.priority - b.priority)
                  .map((tier) => (
                    <Card
                      key={tier.id}
                      className={`relative ${tier.isRecommended ? "border-2 shadow-xl" : "hover:shadow-lg"}`}
                      style={tier.isRecommended ? { borderColor: primaryColor } : {}}
                    >
                      {tier.isRecommended && (
                        <Badge
                          className="absolute -top-3 left-1/2 -translate-x-1/2 text-white"
                          style={{ backgroundColor: primaryColor }}
                        >
                          <Star className="w-3 h-3 mr-1" />
                          Recommended
                        </Badge>
                      )}
                      <CardHeader className="text-center">
                        <Bot className="h-8 w-8 mx-auto mb-2" style={{ color: primaryColor }} />
                        <CardTitle>{tier.displayName}</CardTitle>
                        <CardDescription>{tier.description}</CardDescription>
                        <div className="mt-3">
                          <span className="text-3xl font-bold">${tier.monthlyMinimum}</span>
                          <span className="text-gray-500">/mo min</span>
                        </div>
                        <p className="text-sm text-gray-500">{tier.tokensIncluded.toLocaleString()} tokens included</p>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        {tier.enableJournalAI && (
                          <div className="flex items-center gap-2">
                            <CheckCircle className="h-4 w-4 flex-shrink-0" style={{ color: primaryColor }} />
                            <span className="text-sm">AI Journal Insights</span>
                          </div>
                        )}
                        {tier.enableGoalSuggestions && (
                          <div className="flex items-center gap-2">
                            <CheckCircle className="h-4 w-4 flex-shrink-0" style={{ color: primaryColor }} />
                            <span className="text-sm">Smart Goal Suggestions</span>
                          </div>
                        )}
                        {tier.enableInsights && (
                          <div className="flex items-center gap-2">
                            <CheckCircle className="h-4 w-4 flex-shrink-0" style={{ color: primaryColor }} />
                            <span className="text-sm">Productivity Insights</span>
                          </div>
                        )}
                        {tier.enableAdvancedAnalytics && (
                          <div className="flex items-center gap-2">
                            <CheckCircle className="h-4 w-4 flex-shrink-0" style={{ color: primaryColor }} />
                            <span className="text-sm">Advanced Analytics</span>
                          </div>
                        )}
                        <Button
                          className="w-full mt-2 text-white"
                          style={{ backgroundColor: primaryColor }}
                          onClick={() => handleSubscribe(tier.id)}
                          disabled={selectedPlan === tier.id}
                        >
                          {selectedPlan === tier.id ? "Processing…" : "Choose Plan"}
                        </Button>
                      </CardContent>
                    </Card>
                  ))}
              </div>
            </div>
          )}

          <p className="text-center text-sm text-gray-400 dark:text-gray-500 mt-16">
            All plans include a free trial. No credit card required to start. Cancel anytime.
          </p>
        </div>
      </section>
    </div>
  );
}
